import { IconProp } from '@fortawesome/fontawesome-svg-core'
import {
  faFileCircleExclamation,
  faFileCode,
  faFileLines,
  faFilePdf,
  faFileZipper,
  faImage,
  faMusic,
} from '@fortawesome/free-solid-svg-icons'
import ts, { transpile } from 'typescript'
import { NestedObject, Tag, TagSubset } from '../types/tags'
import { useTagsStore } from '../store/tagsStore'
import defaultProviderImage from '../assets/img/providers/default.jpg'
import { IImage } from '../data/common/commonData'
import { BASE_URL } from '../api/apiHandler'

export const getItemFromStore = (
  key: string,
  defaultValue?: string | boolean,
  store = localStorage
) => {
  try {
    return store.getItem(key) === null
      ? defaultValue
      : JSON.parse(store.getItem(key) as string)
  } catch {
    return store.getItem(key) || defaultValue
  }
}

export const cleanText = (string: string) =>
  (string.charAt(0).toUpperCase() + string.slice(1))
    .replace(/-/g, ' ')
    .replace('_and_', '&')

export const setItemToStore = (
  key: string,
  payload: string,
  store = localStorage
) => store.setItem(key, payload)

export const capitalize = (string: string) =>
  (string.charAt(0).toUpperCase() + string.slice(1))
    .replace(/-/g, ' ')
    .replace('_and_', '&')

export const snakeCase = (string: string) => {
  return string
    .replace(/\W+/g, ' ')
    .split(/ |\B(?=[A-Z])/)
    .map((word) => word.toLowerCase())
    .join('_')
}

export const getColor = (name: string) => {
  const dom = document.documentElement
  return getComputedStyle(dom).getPropertyValue(`--phoenix-${name}`).trim()
}

/* get Dates between */
export const getDates = (
  startDate: Date,
  endDate: Date,
  interval: number = 1000 * 60 * 60 * 24
): Date[] => {
  const duration = +endDate - +startDate
  const steps = duration / interval
  return Array.from(
    { length: steps + 1 },
    (_v, i) => new Date(startDate.valueOf() + interval * i)
  )
}

export const getPastDates = (
  duration: 'week' | 'month' | 'year' | number
): Date[] => {
  let days

  switch (duration) {
    case 'week':
      days = 7
      break
    case 'month':
      days = 30
      break
    case 'year':
      days = 365
      break

    default:
      days = duration
  }

  const date = new Date()
  const endDate = date
  const startDate = new Date(new Date().setDate(date.getDate() - (days - 1)))
  return getDates(startDate, endDate)
}

export const currencyFormat = (
  amount: number,
  options: Intl.NumberFormatOptions = {}
) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'usd',
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
    ...options,
  }).format(amount)
}

export const formatHashtag = (tag: string) =>
  `#${tag.charAt(0).toUpperCase()}${tag.slice(1)}`;


export const getNumbersInRange = (startAt: number, endAt: number) => {
  return [...Array(endAt + 1 - startAt).keys()].map((i) => i + startAt)
}

export const numberFormat = (
  number: number,
  notation: 'standard' | 'compact' = 'standard'
) =>
  new Intl.NumberFormat('en-US', {
    notation,
  }).format(number)

/* Get Random Number */
export const getRandomNumber = (min: number, max: number) => {
  return Math.floor(Math.random() * (max - min) + min)
}

export const getFileIcon = (fileFormat: string): IconProp => {
  switch (fileFormat) {
    case 'zip':
    case 'rar':
      return faFileZipper
    case 'bat':
      return faFileCode
    case 'txt':
      return faFileLines
    case 'mad':
      return faFileCircleExclamation
    case 'wav':
      return faMusic
    case 'pdf':
      return faFilePdf
    case 'jpg':
    case 'png':
    case 'jpeg':
      return faImage
    default:
      return faFileLines
  }
}

export const getIntegerArrayBetween = (start = 0, end: number): number[] =>
  new Array(end + 1 - start).fill(1).map((_, i) => i + start)

export const parseData = (data: string) => {
  try {
    return JSON.parse(data)
  } catch {
    return data
  }
}

export const hexToRgb = (hex: string) => {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return [r, g, b]
}

export const rgbaColor = (color = '#fff', alpha = 0.5) =>
  `rgba(${hexToRgb(color)}, ${alpha})`

export const transformTSCode = (
  snippet: string,
  target: ts.ScriptTarget = ts.ScriptTarget.ES2015
) =>
  transpile(snippet, {
    jsx: ts.JsxEmit.React,
    target,
  })

export const getFileExtension = (fileName: string, separator = '.') =>
  fileName.split(separator).pop() || 'unknown'

export const isImageFile = (file: File) => {
  const imageMimeTypes = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/bmp',
    'image/webp',
  ]
  return imageMimeTypes.includes(file.type)
}

export const convertFileToAttachment = (file: File) => ({
  name: file.name,
  size: `${(file.size / 1024).toFixed(2)} KB`,
  format: getFileExtension(file.name),
  preview: isImageFile(file) ? URL.createObjectURL(file) : undefined,
})

export const getProgressColorVariant = (value: number) => {
  if (value < 21) {
    return 'warning'
  } else if (value < 41) {
    return 'info'
  } else if (value < 81) {
    return 'primary'
  } else {
    return 'success'
  }
}

export const getPriorityColor = (priority: string) => {
  switch (priority.toLowerCase()) {
    case 'urgent':
      return 'danger'
    case 'high':
      return 'warning'
    case 'medium':
      return 'success'
    case 'low':
      return 'info'
    default:
      return 'primary'
  }
}

export const invertHex = (hex: string) =>
  `#${(Number(`0x1${hex.slice(1)}`) ^ 0xffffff)
    .toString(16)
    .substring(1)
    .toUpperCase()}`

export const getColorByBgColor = (bgColor: string) => {
  const rgba = hexToRgb(bgColor)
  if (rgba[0] * 0.299 + rgba[1] * 0.587 + rgba[2] * 0.114 > 186) {
    return 'black'
  } else {
    return 'white'
  }
}

export function transformData(items: Tag[]): NestedObject {
  const result: NestedObject = {};

  items.forEach((item) => {
    const codeParts = item.code.split("|");
    const path = codeParts.slice(0, -1);
    let currentLevel: NestedObject = result;

    path.forEach((part) => {
      if (!currentLevel[part] || Array.isArray(currentLevel[part])) {
        currentLevel[part] = {};
      }
      currentLevel = currentLevel[part] as NestedObject;
    });

    const lastLevelKey = codeParts[codeParts.length - 1];
    if (!Array.isArray(currentLevel[lastLevelKey])) {
      currentLevel[lastLevelKey] = [];
    }

    (currentLevel[lastLevelKey] as TagSubset[]).push({
      id: item.id,
      name: item.name,
    });
  });

  const flattenLastLevel = (obj: any) => {
    for (const key in obj) {
      if (Array.isArray(obj[key])) continue;
      if (typeof obj[key] === "object") {
        const childKeys = Object.keys(obj[key]);
        const isAllArrays = childKeys.every((k) => Array.isArray(obj[key][k]));

        if (isAllArrays) {
          obj[key] = childKeys.flatMap((k) => obj[key][k]);
        } else {
          flattenLastLevel(obj[key]);
        }
      }
    }
  };

  flattenLastLevel(result);

  return result;
}

export function flattenProducts(data: {
  Producto: {
    Categorias: {
      [categoria: string]: { id: number; name: string }[];
    };
  };
}): { id: number; name: string; category: string }[] {
  const result: { id: number; name: string; category: string }[] = [];

  for (const categoria in data?.Producto?.Categorias) {
    const items = data.Producto.Categorias[categoria];
    for (const item of items) {
      result.push({ ...item, category: categoria });
    }
  }

  return result;
}


export function getTag(data: NestedObject | Array<{ id: number; name: string }> | undefined, search: number | string): TagSubset | undefined {
  const arrayData = Array.isArray(data) ? (data as TagSubset[]) : [];
  if (typeof search === "number") {
    return arrayData.find((item) => item.id === search);
  }
  return arrayData.find((item) => item.name === search);
}

export function isTagArray(data: NestedObject | Array<{ id: number; name: string }> | undefined): Array<{ id: number; name: string }> | undefined {
  return Array.isArray(data) ? data : undefined;
}

export function isVerifiedUser(tags: number[]): boolean {
  const { providerVerifiedUser } = useTagsStore.getState();
  if (!providerVerifiedUser) return false;

  return tags.includes(providerVerifiedUser.id);
}

function getTagNamesFromStore(tags: number[], tagSource: Array<{ id: number; name: string }> | undefined): string[] {
  return tags
    .map((tag) => getTag(tagSource, tag))
    .filter((tag) => tag !== undefined)
    .map((tag) => tag.name);
}

function getTagIdsFromStore(tags: string[], tagSource: Array<{ id: number; name: string }> | undefined): number[] {
  return tags
    .map((tag) => getTag(tagSource, tag))
    .filter((tag) => tag !== undefined)
    .map((tag) => tag.id);
}

export function getProviderCategories(tags: number[]): string[] {
  const { providerCategories } = useTagsStore.getState();
  return getTagNamesFromStore(tags, providerCategories);
}

export function getProviderCategoriesByName(categories: string[]): number[] {
  const { providerCategories } = useTagsStore.getState();
  return getTagIdsFromStore(categories, providerCategories);
}

export function getProductCategory(tagId: number): string {
  const { productCategories } = useTagsStore.getState();
  for (const key in productCategories) {
    if (productCategories[key].some(({ id }: { id: number }) => id === tagId)) {
      return key;
    }
  }
  return '';
}

export function getLocations(tags: number[]): string[] {
  const { providerLocation } = useTagsStore.getState();
  return getTagNamesFromStore(tags, providerLocation);
}

export function getLocationsByName(locations: string[]): number[] {
  const { providerLocation } = useTagsStore.getState();
  return getTagIdsFromStore(locations, providerLocation);
}

export function getSubCategories(tags: number[]): string[] {
  const { subProductosTags } = useTagsStore.getState();
  return getTagNamesFromStore(tags, subProductosTags);
}

export const getImageUrl = (id: string) => {
  return `${BASE_URL}/img/${id}`;
};

export const getImageByPosition = async (
  imageId: IImage,
  thumb?: boolean,
  fallbackImage?: string,
): Promise<string> => {
  const defaultImage = fallbackImage ?? defaultProviderImage;
  if (!imageId) return defaultImage;

  const imageUrl = getImageUrl(thumb ? imageId.thumbnailId : imageId.imageId);

  try {
    const response = await fetch(imageUrl, {
      method: 'GET',
      headers: {
        'Range': 'bytes=0-0',
      },
    });

    if (!response.ok) {
      return defaultImage;
    }

    return imageUrl;
  } catch {
    return defaultImage;
  }
};

export const getOrderObject = (orderBy: string) => {
  const order = orderBy.split('-');

  if (order.length > 1) {
    return {
      orderBy: order[0],
      orderType: order[1],
    }
  }

  switch (orderBy) {
    case 'newest':
      return {
        orderBy: 'createdAt',
        orderType: 'desc',
      };
    case 'oldest':
      return {
        orderBy: 'createdAt',
        orderType: 'asc',
      };
    default:
      return {
        orderBy: 'createdAt',
        orderType: 'desc',
      };
  }
}


export const getRating = (rating: number) => {
  const stars = (rating / 100) * 5;
  return Math.round(stars * 2) / 2;
}